import { MenuOptions } from "@qivia/ui/src/designSystem/components/Menu/MenuOptions";
import { SideBarCompanies, TextCapitalized } from "@qivia/ui";
import { Radio } from "@qivia/ui/src/designSystem/components/Radio";
import { Spacer } from "@qivia/ui/src/designSystem/components/Spacer";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useOutsideClick } from "@qivia/ui/src/hooks/useOutsideClick";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { selectManager } from "../routes/home/homeSlice";
import {
  getDepartmentsByManagerAsync,
  selectGetDepartmentsByManagerStatus,
} from "../routes/homeDepartments/departments/departmentsSlice";
import "../i18n/i18n";

type DepartmentSelectorProps = {
  departments: { name: string; uuid: string }[];
  onClickDepartmentCreation: () => void;
  currentDepartment: string;
  onChangeDepartment: (departmentUuid: string) => void;
  size: "L" | "S";
};

export const DepartmentSelector = (props: DepartmentSelectorProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isMenuOptionsDepartmentsOpen, setIsMenuOptionsDepartmentsOpen] =
    useState<boolean>(false);
  const [listDisplayed, setListDisplayed] = useState<
    { name: string; uuid: string }[]
  >([]);

  const outsideDepartmentsMenuOptions = useOutsideClick(() => {
    setIsMenuOptionsDepartmentsOpen(false);
  });

  const manager = useAppSelector(selectManager);
  const loadDepartmentsByManagerStatus = useAppSelector(
    selectGetDepartmentsByManagerStatus,
  );

  useEffect(() => {
    if (manager && loadDepartmentsByManagerStatus === "idle") {
      void dispatch(
        getDepartmentsByManagerAsync({
          departmentUuid: manager.departmentsAccess?.[0] || "",
        }),
      );
    }
  }, [loadDepartmentsByManagerStatus, dispatch, manager]);

  useEffect(() => {
    setListDisplayed(props.departments);
  }, [props.departments]);

  const searchBarProps = {
    values: props.departments,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  return (
    <StyledMenu ref={outsideDepartmentsMenuOptions}>
      <StyledMenuOptionDepartments>
        {isMenuOptionsDepartmentsOpen && (
          <>
            {props.size === "L" ? <Spacer x={2} /> : <Spacer x={11} />}
            <StyledColumn>
              <MenuOptions
                hasHoverBackground
                options={listDisplayed.map((department) => {
                  return {
                    action: () => {
                      props.onChangeDepartment(department.uuid);
                    },
                    label: (
                      <StyledWordBreak>
                        <StyledJustify>
                          <StyledColumn>
                            <StyledDepartmentsTitle>
                              <TextCapitalized>
                                {t(`menuOption.departments`)}
                              </TextCapitalized>
                            </StyledDepartmentsTitle>
                            <TextCapitalized>{department.name}</TextCapitalized>
                          </StyledColumn>
                          <Radio
                            isSelected={
                              props.currentDepartment === department.uuid
                            }
                          />
                        </StyledJustify>
                        <Spacer x={0.25} />
                      </StyledWordBreak>
                    ),
                    icon: (
                      <SideBarCompanies
                        text={department.name}
                        version={"S"}
                        onClick={() => {
                          props.onChangeDepartment(department.uuid);
                        }}
                        hasFocusBorder={true}
                      />
                    ),
                  };
                })}
                searchBar={searchBarProps}
                width="20"
                searchBarPlaceholder={t("menuOptions.searchbar.department")}
              />
            </StyledColumn>
          </>
        )}
      </StyledMenuOptionDepartments>
      <SideBarCompanies
        text={
          props.departments.find(
            (dept) => dept.uuid === props.currentDepartment,
          )?.name || ""
        }
        version={props.size}
        onClick={() =>
          setIsMenuOptionsDepartmentsOpen(!isMenuOptionsDepartmentsOpen)
        }
        hasFocusBorder={isMenuOptionsDepartmentsOpen}
        title={t("sideBar.title.department")}
      />
    </StyledMenu>
  );
};

const StyledWordBreak = styled.div`
  display: flex;
  word-break: break-all;
`;

const StyledJustify = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledDepartmentsTitle = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/text/lightGrey"]};
  ${typographies["Body/XS"]};
`;

const StyledMenu = styled.div`
  width: 100%;
`;

const StyledMenuOptionDepartments = styled.div`
  z-index: 500;
  position: absolute;
  top: 6rem;
`;
